// What each location page needs, as legacy's router declares it (`meta.permission`, router/routes/users/*.ts; parity
// backoffice/permissions.md §3). Legacy merged a child route's meta over its parent's, so a role that held a child key
// but not its parent (Sales Reports without View Reports, Online Store Orders without Manage Online Store, Modifiers
// without Manage Items) opened the page and then every call on it failed on the parent's API check (B-row). Here a page
// lists both and is refused, as legacy refuses any page the role lacks. The dashboard is never refused (View Dashboard
// is locked on). The server checks every operation regardless; this is what the router shows.
import type { PermissionKey } from '@radish/contracts';
import type { AppPath } from '../router-paths.ts';

type Need = readonly [PermissionKey, ...PermissionKey[]];

const ITEMS: Need = ['items.manage'];
const DISCOUNTS: Need = ['discounts.manage'];
const EMPLOYEES: Need = ['labor.timecards.manage'];
const CUSTOMERS: Need = ['customers.manage'];
const ONLINE_SETTINGS: Need = ['online.manage', 'online.settings.manage'];
const SALES: Need = ['reports.view', 'reports.sales.view'];
const AUDITING: Need = ['reports.view', 'reports.auditing.view'];
const PAYROLL: Need = ['reports.view', 'reports.payroll.view'];
const HARDWARE: Need = ['hardware.manage'];
const LOCATION: Need = ['settings.location.manage'];
const STAFF: Need = ['settings.staff.manage'];
const ORDER_CHECKOUT: Need = ['settings.order_checkout.manage'];

export const PAGE_PERMISSIONS: Readonly<Partial<Record<AppPath, Need>>> = {
  '/l/$slug/discounts/preset': DISCOUNTS,
  '/l/$slug/discounts/auto': DISCOUNTS,
  '/l/$slug/discounts/price-adjustments': DISCOUNTS,
  '/l/$slug/menu/items/{-$itemId}': ITEMS,
  '/l/$slug/menu/categories': ITEMS,
  '/l/$slug/menu/modifiers': ['items.manage', 'modifiers.manage'],
  '/l/$slug/menu/departments': ITEMS,
  '/l/$slug/menu/display-reorder': ['items.manage', 'items.displayOrder.manage'],
  '/l/$slug/menu/visibility': ITEMS,
  '/l/$slug/employees/list': EMPLOYEES,
  '/l/$slug/employees/timecards/{-$timecardId}': EMPLOYEES,
  '/l/$slug/employees/schedule': EMPLOYEES,
  '/l/$slug/employees/schedule/templates': EMPLOYEES,
  '/l/$slug/employees/availability': EMPLOYEES,
  '/l/$slug/employees/time-off': EMPLOYEES,
  '/l/$slug/employees/shift-swaps': EMPLOYEES,
  '/l/$slug/employees/shift-claims': EMPLOYEES,
  '/l/$slug/employees/$employeeId': EMPLOYEES,
  '/l/$slug/settings/staff-settings/employee-roles': STAFF,
  '/l/$slug/customers/list': CUSTOMERS,
  '/l/$slug/customers/$customerId': CUSTOMERS,
  '/l/$slug/floor-plans': ['floor.manage'],
  '/l/$slug/order/lookup': ['order_lookup.view'],
  // Legacy route meta `manage_qr_codes` on every QR Table Pay page.
  '/l/$slug/qr-table-pay/qr-codes': ['qr.manage'],
  '/l/$slug/qr-table-pay/templates': ['qr.manage'],
  '/l/$slug/qr-table-pay/settings': ['qr.manage'],
  '/l/$slug/online-store/info': ONLINE_SETTINGS,
  '/l/$slug/online-store/orders': ['online.manage', 'online.orders.manage'],
  '/l/$slug/online-store/hours-and-closures': ONLINE_SETTINGS,
  '/l/$slug/online-store/prep-times': ONLINE_SETTINGS,
  '/l/$slug/online-store/banners': ['online.manage', 'online.banners.manage'],
  '/l/$slug/online-store/notifications': ONLINE_SETTINGS,
  '/l/$slug/online-store/voice-ordering': ONLINE_SETTINGS,
  '/l/$slug/online-store/voice-conversations': ONLINE_SETTINGS,
  '/l/$slug/online-store/voice-conversations/$conversationId': ONLINE_SETTINGS,
  // Legacy: admins and resellers only (REQUIRES_ADMIN_RESELLER), which the page itself enforces.
  '/l/$slug/online-store/api-keys': ['online.manage'],
  '/l/$slug/reports/sales/overview': SALES,
  '/l/$slug/reports/sales/consolidated': SALES,
  '/l/$slug/reports/sales/items': SALES,
  '/l/$slug/reports/sales/category': SALES,
  '/l/$slug/reports/sales/department': SALES,
  '/l/$slug/reports/sales/employee': SALES,
  '/l/$slug/reports/sales/dining': SALES,
  '/l/$slug/reports/sales/time-segment': SALES,
  '/l/$slug/reports/sales/discounts': SALES,
  '/l/$slug/reports/sales/gift-cards': SALES,
  '/l/$slug/reports/sales/gift-cards/welcome': SALES,
  '/l/$slug/reports/audit/drawer': AUDITING,
  '/l/$slug/reports/audit/server-banks': AUDITING,
  '/l/$slug/reports/audit/drawer/$tillSessionId': AUDITING,
  '/l/$slug/reports/audit/adjustments/voids': AUDITING,
  '/l/$slug/reports/audit/adjustments/deletion': AUDITING,
  '/l/$slug/reports/audit/refunds': AUDITING,
  '/l/$slug/reports/management/payroll': PAYROLL,
  '/l/$slug/reports/management/breaks': PAYROLL,
  '/l/$slug/settings/hardware/devices': HARDWARE,
  '/l/$slug/settings/hardware/printers': HARDWARE,
  '/l/$slug/settings/location/business-profile': LOCATION,
  '/l/$slug/settings/location/business-profile/business-hours': LOCATION,
  '/l/$slug/settings/location/business-profile/branding': LOCATION,
  '/l/$slug/settings/location/payment-configuration': LOCATION,
  // SCR-129 (billing plan §4.7): staff and the account's reseller set billing up; a merchant with Location Settings
  // reads it (slice 5, G-B2). The menu entry stays staff's until GATE-08 shows it for a location on a real plan (slice 6).
  '/l/$slug/settings/location/plans-and-billing': LOCATION,
  '/l/$slug/settings/location/business-day': ['settings.business_day.manage'],
  '/l/$slug/settings/location/users': ['users.manage'],
  // Legacy `requiresSuperAdmin` on both (A53): `users.manage` is Super Admin only, never a custom role's.
  '/l/$slug/settings/staff-settings/user-roles': ['users.manage'],
  // Radish staff only at cutover (G-M19, Q-SET-10: legacy shows Scheduling to platform admins only).
  '/l/$slug/settings/staff-settings/scheduling': ['settings.staff.manage', 'settings.platform.manage'],
  '/l/$slug/settings/staff-settings/timecards/employee-breaks': STAFF,
  '/l/$slug/settings/staff-settings/timecards/report-settings': STAFF,
  '/l/$slug/settings/staff-settings/timecards/overtime': STAFF,
  '/l/$slug/settings/order-checkout/pos-functions/register-settings': ORDER_CHECKOUT,
  '/l/$slug/settings/order-checkout/pos-functions/drawers': ORDER_CHECKOUT,
  '/l/$slug/settings/order-checkout/pos-functions/voids': ORDER_CHECKOUT,
  '/l/$slug/settings/order-checkout/pos-functions/dining-options': ORDER_CHECKOUT,
  '/l/$slug/settings/order-checkout/pos-functions/tender-types': ORDER_CHECKOUT,
  '/l/$slug/settings/order-checkout/pos-functions/pacing': ORDER_CHECKOUT,
  // Legacy gates every pos-functions page on Order & Checkout Settings, but its id-verifications API checked no permission
  // at all, so any member of the location could change which items ask for an ID (bug B496, L686). Next had left the page
  // open to every member. Its writes are list and item upserts, which the server keeps on Manage Items, as Departments.
  '/l/$slug/settings/order-checkout/pos-functions/id-verification': ['settings.order_checkout.manage', 'items.manage'],
  '/l/$slug/settings/order-checkout/print-options': ORDER_CHECKOUT,
  '/l/$slug/settings/order-checkout/tips/customer-tips': ORDER_CHECKOUT,
  '/l/$slug/settings/order-checkout/tips/staff-tips': ORDER_CHECKOUT,
  '/l/$slug/settings/reporting/time-segments': ['settings.reporting.manage'],
  // Legacy gated this by Reporting Settings alone, but every departments call needs Manage Items (bug B192, SCR-161).
  '/l/$slug/settings/reporting/departments': ['settings.reporting.manage', 'items.manage'],
};

/**
 * The pages legacy opens to every signed-in member of the location: the dashboard (View Dashboard is locked on), the
 * profile and Access Denied. Every other location page has an entry above (test/backoffice-permissions.test.tsx).
 */
export const OPEN_PAGES: readonly AppPath[] = ['/l/$slug', '/l/$slug/profile', '/l/$slug/access-denied'];

/**
 * Whether a principal holding `can` may open the page at `path`. It fails closed: a page named in neither table, a
 * route that is not a page (`null`), and an entry needing nothing are all refused (review of #1387).
 */
export function mayOpen(
  path: AppPath | null, can: (p: PermissionKey) => boolean, table: Readonly<Partial<Record<AppPath, readonly PermissionKey[]>>> = PAGE_PERMISSIONS,
): boolean {
  if (path === null) return false;
  if (OPEN_PAGES.includes(path)) return true;
  const need = table[path];
  return need !== undefined && need.length > 0 && need.every(can);
}
